import React, { useEffect, useState, useMemo, useRef } from 'react';
import { buildEngine } from '../../engine/Engine';
import { buildFieldSuggestions, buildStandaloneSearchBox } from '@coveo/headless';
import HeaderSearchBox from './HeaderSearchBox';
import SearchContext from '../../state/context';
import useOutsideClick from '../../../hooks/useClickOutside';
import CoveoSearchInput from '../../components/CoveoSearchInput';

const HeaderSearch = ({
  coveoNumberOfSuggestions = '4',
  resultsPageLinkLabel = 'All Results',
  suggestedResultsLabel = 'Suggested Results',
  placeholder = 'Type to search...',
  coveoSearchResultsPageUrl = '/',
  coveoOrganizationId,
  coveoAccessToken,
  coveoSearchHub,
  coveoPipeline,
  allowEmptyQueries
}) => {
  const elementRef = useRef(null);
  const engine = useMemo(() => {
    return buildEngine({
      organizationId: coveoOrganizationId,
      accessToken: coveoAccessToken,
      searchHub: coveoSearchHub,
      pipeline: coveoPipeline
    });
  }, []);

  const searchController = useMemo(
    () =>
      buildStandaloneSearchBox(engine, {
        options: {
          numberOfSuggestions: 0,
          redirectionUrl: coveoSearchResultsPageUrl
        }
      }),
    []
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchState, setSearchState] = useState(searchController.state);
  const [searchInputValue, setSearchInputValue] = useState('');

  const handleClickedSuggestions = suggestion => {
    searchController.updateText(suggestion);
    searchController.submit();
  };

  const suggestionsController = useMemo(
    () =>
      buildFieldSuggestions(engine, {
        options: {
          facet: {
            field: 'ssl_heading',
            facetId: 'ssl-heading-1'
          }
        }
      }),
    []
  );

  useEffect(() => {
    searchController.subscribe(() => {
      setSearchState(searchController.state);

      if (searchController.state.redirectTo) {
        const { redirectTo, value, analytics } = searchController.state;
        const data = JSON.stringify({ value, analytics });
        localStorage.setItem('coveo_standalone_search_box_data', data);

        if (redirectTo !== coveoSearchResultsPageUrl) {
          window.location.href = redirectTo;
        } else {
          window.location.href = redirectTo;
        }
      }
    });
  }, []);

  const handleSearchButtonClicked = () => {
    if (isExpanded) {
      ref.current.parentElement.classList.remove('header-search-headless--active');
      ref.current.closest('.header')?.classList?.remove('header-search-headless--active');
    } else {
      ref.current.parentElement?.classList?.add('header-search-headless--active');
      ref.current.closest('.header')?.classList?.add('header-search-headless--active');
      setTimeout(() => { ref.current.querySelector('input').focus(); }, 1000);
    }
    setIsExpanded(!isExpanded);
  };

  const ref = useOutsideClick(() => {
    ref.current.parentElement?.classList?.remove('header-search-headless--active');
    ref.current.closest('.header')?.classList?.remove('header-search-headless--active');
    setIsExpanded(false);
  });

  return (
    <>
      <SearchContext.Provider
        value={{
          engine: engine,
          onClickedSuggestion: handleClickedSuggestions,
          numberOfSuggestions: coveoNumberOfSuggestions,
          resultsPageUrl: coveoSearchResultsPageUrl,
          resultsPageLinkLabel: resultsPageLinkLabel
        }}
      >
        <div ref={ref} className="header-search-headless__coveo-search-bar">
          <button className="header-search-headless__coveo-search-btn" aria-label="Search" onClick={() => { handleSearchButtonClicked(); }}>
            <svg viewBox="0 0 31 33" height="30" role="presentation">
              <use xlinkHref="#search"></use>
            </svg>
            <span className="header-search-headless__label">Search</span>
          </button>

          <CoveoSearchInput
            coveoNumberOfSuggestions={coveoNumberOfSuggestions}
            coveoEngine={engine}
            searchShowIcon={false}
            useButton={false}
            searchInputPlaceholder={placeholder}
            allowEmptyQueries={allowEmptyQueries === 'true' ? true : false}
            didYouMeanString="Did you mean"
            coveoRedirectUrl="/search"
          />

          <button className="header-search-headless__coveo-close-btn" aria-label="Close Search"
            onClick={() => {
              searchController.clear();
              ref.current.parentElement.classList.remove('header-search-headless--active');
              ref.current.closest('.header').classList.remove('header-search-headless--active');
              setSearchInputValue('');
              setIsExpanded(false);
            }}
          >
            <svg viewBox="0 0 25 25" height="25" role="presentation">
              <use xlinkHref="#close"></use>
            </svg>
          </button>
        </div>
      </SearchContext.Provider>
    </>
  );
};

export default HeaderSearch;